// @ts-strict-ignore
import type * as React from 'react';
import { useLocation, useRouteMatch } from 'react-router-dom';

import {
  OVERLAY_TYPES,
  useParseOverlayQueryParams,
} from '@/components/AppOverlay';
import type { PreviewParamsType } from '@/components/Preview/OverlayPreview/constants';
import { useTrackSourceCards } from '@/components/SourceCards/hooks/useTrackSourceCards';
import { useShowAskNeedl } from '@/features/ChatBotV2/hooks/useShowAskNeedl';
import { useIsCommentsFeed } from '@/features/Comment';
import { useGetFeedCardStatus } from '@/features/CustomFeedV2/hooks/useGetFeedCardStatus';
import { useResponsiveConstants } from '@/hooks/responsive/useResponsiveConstants';
import { useQueryParams } from '@/hooks/useQueryParams';
import { useSourceClick } from '@/hooks/useSourceClick';
import { useNavigate } from '@/router';
import type { FeedItemType, HandlersType } from '@/types';
import { getCommonThumbnail } from '@/utils/getCommonThumbnail';
import { getLogo } from '@/utils/getIcons';
import { getMimeIcon } from '@/utils/getMimeIcons';

export const useFeedCard = (
  result: FeedItemType,
  handlers: HandlersType,
  index: number
) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { url } = useRouteMatch();
  const [q] = useQueryParams();

  const { desktop } = useResponsiveConstants();
  const showAskNeedl = useShowAskNeedl();

  const { linkToSource, openSourceClickHandler } = useSourceClick(result);
  const { trackSourceCard } = useTrackSourceCards(result);
  const { isGlobalComments } = useIsCommentsFeed();
  const { readStatus } = useGetFeedCardStatus(result);
  const overlayParams = useParseOverlayQueryParams() as PreviewParamsType;
  const { deeplink: overlayDeeplink, global_channel_id } = overlayParams;

  const {
    category,
    source,
    deeplink,
    access_key,
    board_id,
    channel_id,
    thumbnail_location,
    label,
    mime_type,
    sources,
    plain_text,
    content,
    category_tab,
    unix,
    selected,
    doc_id,
    comment_id,
    object_location,
  } = result;

  const BannerImage = ['clipped_reports'].includes(source)
    ? getMimeIcon(mime_type?.toLowerCase() ?? '')
    : ['documents', 'media'].includes(source)
    ? getLogo(sources ? sources[0] : '')
    : category === 'links'
    ? getLogo('links')
    : thumbnail_location
    ? getCommonThumbnail(thumbnail_location, label || 'rss icon')
    : getLogo(source ? source.toLowerCase() : '', 'rss');

  /**
   *
   * @param comments - true if called from <CommentActionButton /> (in sourcecard)
   */
  const onClickHandler = ({ comments }: ClickHandlerArgs = {}) => {
    if (!comments) {
      trackSourceCard();
    }

    if (desktop) {
      handleOverlayClick({ comments });
    } else {
      const searchParams = new URLSearchParams(location.search);

      if (comments) {
        searchParams.set('comments', 'true');
      }

      if (isGlobalComments || comments) {
        searchParams.set('comment_id', comment_id);
      }

      if (access_key) {
        searchParams.set('access_key', access_key);
      }

      if (board_id) {
        searchParams.set('board_id', board_id);
      }

      if (channel_id) {
        searchParams.set('channel_id', channel_id);
      }

      if (category_tab) {
        searchParams.set('category_tab', category_tab);
      }

      navigate.push(
        {
          pathname: `/preview/${source}/${deeplink}`,
          search: `${searchParams}`,
        },
        { location, ...location.state },
        {
          removeOldQueryParams: true,
          removeOldState: true,
        }
      );
    }
  };

  const handleOverlayClick = ({ comments }: ClickHandlerArgs) => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('overlay-type', OVERLAY_TYPES.Preview);

    if (comments) {
      searchParams.set('overlay-comments', 'true');
    }

    if (isGlobalComments || comments) {
      searchParams.set('overlay-comment_id', comment_id);
    }

    if (access_key) {
      searchParams.set('overlay-access_key', access_key || '');
    }

    if (board_id) {
      searchParams.set('overlay-board_id', board_id);
    }

    if (channel_id) {
      searchParams.set('overlay-channel_id', channel_id);
    }

    if (isGlobalComments && channel_id) {
      searchParams.set('overlay-global_channel_id', channel_id);
    }

    if (showAskNeedl) {
      searchParams.set('overlay-preview_askneedl', 'true');
    }

    searchParams.set('overlay-source', source || '');

    searchParams.set('overlay-category', category || '');

    searchParams.set('overlay-category_tab', category_tab || '');

    searchParams.set('overlay-deeplink', deeplink);

    navigate.push(
      {
        pathname: url,
        search: `${searchParams}`,
      },
      null,
      { removeOldQueryParams: true, removeOldState: false }
    );
  };

  const toggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    handlers.toggleHandler(index);
  };

  const shareHandler = (e: React.MouseEvent) => {
    e.stopPropagation();
    handlers.shareHandler({
      category,
      deeplink,
      access_key,
      source,
      plain_text,
      content,
      ...(channel_id && { channel_id, board_id }),
      unix,
      selected,
      doc_id,
      object_location,
    });
  };

  const quickRemovalHandler = (e: React.MouseEvent) => {
    e.stopPropagation();
    handlers.quickRemovalHandler(result);
  };

  const deleteHandler = (e: React.MouseEvent) => {
    e.stopPropagation();
    handlers.deleteDocumentHandler(index);
  };

  const tableExtractionHandler = (e: React.MouseEvent) => {
    e.stopPropagation();
    handlers.tableExtractionHandler(result);
  };

  const isActiveChannelId = isGlobalComments
    ? global_channel_id === channel_id
    : true;

  const active = overlayDeeplink === deeplink && isActiveChannelId;

  return {
    q,
    channel_id,
    BannerImage,
    deleteHandler,
    linkToSource,
    openSourceClickHandler,
    tableExtractionHandler,
    onClickHandler,
    toggle,
    shareHandler,
    quickRemovalHandler,
    active,
    readStatus,
  };
};

export type ClickHandlerArgs = {
  comments?: boolean;
};
